/** @module csi/breadcrumb/Widget */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-construct",
	"dojo/topic",
	"csi",
	"csi/_base/underscore",
	"csi/access/crews",
	"csi/action",
	"csi/action/_StateMachineActions",
	"csi/breadcrumb/Crumb",
	"csi/breadcrumb/CrumbChild",
	"csi/breadcrumb/PhaseDropDownButton",
	"csi/breadcrumb/RevisionDropDownButton",
	"csi/breadcrumb/RevisionVersionDropDownButton",
	"csi/comm",
	"csi/config",
	"csi/def",
	"csi/def/Action",
	"csi/def/ActionFactory",
	"csi/def/Heading",
	"csi/def/ViewRef",
	"csi/heading/_base",
	"csi/heading/stateColorResolver",
	"csi/html",
	"csi/locale",
	"csi/page/SearchManager",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/session",
	"csi/sessionConstants",
	"csi/state/rootStore",
	"csi/toolbar/MultiFilterCondition",
	"csi/view/Toolbar",
	"csi/view/ToolbarPluginButton",
	"csi/view/ToolbarPluginMenu",
	"csi/view/ToolbarPluginRadioButton",
	"csi/view/Widget",
	"csi/widget/MaterialIconWithLink",
	"csi/widget/NameValuePair",
	"csi/xml",
	"dojo/text!csi/templates/breadcrumb.html",

	"csi/heading/Enum",
	"csi/heading/String",
	"SiteAdmin/PhaseRenderer"
], function (declare, lang, domConstruct, topic, csi, _, crews, csiAction, _StateMachineActions, Crumb,
	CrumbChild, PhaseDropDownButton, RevisionDropDownButton, RevisionVersionDropDownButton, comm, config, def, Action, ActionFactory, Heading, ViewRef, csiHeading, stateColorResolver, html,
	locale, SearchManager, reflection, reflectionHints, session, sessionConstants, { localStore, routerStore, sessionStore }, MultiFilterCondition, Toolbar, ToolbarPluginButton,
	ToolbarPluginMenu, ToolbarPluginRadioButton, Widget, MaterialIconWithLink, NameValuePair, xml, template) {

	const ACTION_SIMPLE_APPROVE = "SimplePublish";
	var ACTION_REVISION = "BreadcrumbRevision";
	var ACTION_REVISION_ALL = "BreadcrumbRevisionAll";
	var ACTION_SEPARATOR = "|";
	var COMMON_STATE_ACTIONS = ["|", "PurgeAllPreviousRevisions", "PurgeRevisions", "PurgeResetRevisions", "CancelApprovalWorkflow", "ForceApprove"];
	var NODES_WITH_PRINT_ACTIONS_DROPDOWN = ["RecipeDataSheet"];

	var GLOBAL_MASTER_TYPES = [
		"ContractualDocument",
		"SustainabilityProfile",
		"SubRouting",
		"TestSpec"
	];

	const NODES_WITH_DROPDOWN_ACTIONS = [
		"Material",
		"Style",
	];

	var TEMPLATE_PARENTS = [
		"centric://REFLECTION/INSTANCE/SiteLibSupplierItem/SiteLibSupplierItem"
	];

	var WORKFLOW_ACTION_IDS = {
		Approve: 1,
		EndPhase: 1
	};

	var _headingName = lang.mixin(new Heading(), { m_id: xml.ATTR_NAME });
	var _headingPhase = lang.mixin(new Heading(), { m_id: "Phase" });
	var _headingState = lang.mixin(new Heading(), { m_id: "State" });

	var _RevisionActionPlugin = declare(ToolbarPluginMenu, {
		enablePlugin: function (/*Context?*/ctx) {
			// summary: switch context to selected revision rather than master
			if (ctx) {
				ctx.pushParams().setNodeURL(routerStore.locationRevisionUrl);
			}

			this.inherited(arguments);

			if (ctx) {
				ctx.pop();
			}
		}
	});
	const _stateStartApprovalActionController = "csi/breadcrumb/StartApprovalActionController";
	const _stateActionController = "csi/breadcrumb/StateActionController";
	const _stateApproveConfirmController = "csi/breadcrumb/ApproveConfirmController";
	const _stateMassUpdateDataSheetsController = "csi/breadcrumb/MassUpdateDataSheetsController";
	const _statePurgeController = "csi/breadcrumb/PurgeController";
	const _stateReviseController = "csi/breadcrumb/ReviseReopenController";
	const _stateActions = {
		Approve: _stateActionController,
		ApproveConfirm: _stateApproveConfirmController,
		Abandon: _stateActionController,
		CancelApprovalWorkflow: _stateActionController,
		CancelOffline: _stateActionController,
		CloseRevision: _stateActionController,
		EndPhase: _stateActionController,
		ForceApprove: _stateActionController,
		MakePending: _stateActionController,
		MakePending2: _stateActionController,
		MassUpdateDataSheets: _stateMassUpdateDataSheetsController,
		PurgeAllPreviousRevisions: _statePurgeController,
		PurgeRevisions: _statePurgeController,
		PurgeResetRevisions: _statePurgeController,
		ReopenPhase: _stateReviseController,
		RevertToApproved: _stateActionController,
		RevertToDraft: _stateActionController,
		ReviseAndAbandon: _stateReviseController,
		ReviseAndAbandonCurrent: _stateActionController,
		Revise: _stateReviseController,
		ReviewStart: _stateActionController,
		ReviewApprove: _stateActionController,
		ReviewFail: _stateActionController,
		ReviewCancel: _stateActionController,
		ReviewReopen: _stateActionController,
		StartApprovalWorkflow: _stateStartApprovalActionController
	};

	csiAction.registerControllers("Breadcrumb", _stateActions);
	csiAction.registerControllers("Breadcrumb", {
		New: "csi.breadcrumb.NewController",
		Copy: "csi.breadcrumb.CopyController",
		BreadcrumbRevisionAll: "csi.breadcrumb.RevisionsController"
	});

	/**
	 * Determine list of revision state actions for given master node.
	 * @param {Node} master
	 * @return {Action[]} actions
	 */
	function _getRevisionActions(master) {
		var smActions = _StateMachineActions[master.StateMachine];
		if (!smActions || !xml.nodeHasAccess(master, xml.RIGHT_WRITE, reflection.ATTR_REVISIONS)) {
			return null;
		}

		var rcRev = reflection.getNodeClass(master).getRevisionClass();
		var bWorkflow = _hasAssignedWorkflow(master);

		return _.map(smActions.concat(COMMON_STATE_ACTIONS), function (id) {
			if (id == ACTION_SEPARATOR) {
				return id;
			}

			if (bWorkflow && WORKFLOW_ACTION_IDS[id]) {
				id = "StartApprovalWorkflow";
			}

			// create _StateAction and reset default postAction
			var a = ActionFactory.newAction(id);
			a.classToolbarPlugin = _RevisionActionPlugin;
			a.m_postAction = null;
			a.resolve(null, rcRev);
			return a;
		});
	}

	/**
	 * Determine if master class has an approval workflow.
	 * @param {Node} master
	 * @return {boolean}
	 */
	function _hasAssignedWorkflow(master) {
		return !!config.getNodeSetting(master, "WorkflowName") || !!config.getNodeClassSetting(master, "WorkflowName");
	}

	/**
	 * Render name-value pair for given heading under given DOM node.
	 * @param {Context} ctx
	 * @param {Heading} h
	 * @param {DOMNode} domNode
	 */
	function _renderHeading(ctx, h, domNode) {
		var rev = ctx.getNode();
		var master = ctx.getNodeParent();
		var pdf = comm.getFileHref(rev.PDF, false, locale.getNodeName(master) + (rev.$Name ? (" - " + rev.$Name) : ""));

		new NameValuePair({
			"class": stateColorResolver.getStateCss(rev),
			iconCss: pdf ? "far fa-fw fa-file-pdf" : null,
			iconTitle: locale.resource("OpenPdf"),
			name: "",
			value: locale.capitalizeText(csiHeading.toText(ctx, h)),
			onIcon: function () {
				csi.browserWindow(pdf);
			}
		}).placeAt(domNode);

	}

	/**
	 * Render name-value pair for given heading under given DOM node.
	 * @param {Context} ctx
	 * @param {Heading} h
	 * @param {DOMNode} domNode
	 * @param {Cnl} linkedNodeURL
	 */
	function _renderLinkedHeading(ctx, h, domNode, linkedNode) {
		const linkedNodeName = locale.getNodeConfiguredName(linkedNode);
		const isLinkedNodeApparelBOM = linkedNode.$Type === "ApparelBOM";
		const linkedParentNodeType = isLinkedNodeApparelBOM ? "Theme" : "Shape";
		const linkedParentNodeTypeLocalized = locale.resource(linkedParentNodeType);
		const linkedNodeTypeLocalized = locale.type(isLinkedNodeApparelBOM ? "ProductBOM" : linkedNode.$Type);
		title = new MultiFilterCondition({
			label: locale.resource("GoToLinkedNode", [linkedParentNodeTypeLocalized, linkedNodeTypeLocalized, linkedNodeName]),
		});
		new NameValuePair({
			"class": "csi-state csi-state-linked csiAction",
			iconCss: "material-icons csi-state-linked",
			iconText: "logout",
			title,
			titlePosition: ["below-centered", "above-centered"],
			name: "",
			value: locale.capitalizeText("Linked"),
			onClick: () => {
				session.setLocationUrl(linkedNode.$URL, true);
			}
		}).placeAt(domNode);
	}

	/**
	 * Return true if the node is draft revision
	 * @param {Node} node
	 * @return {boolean}
	 */
	function _isDraft(node) {
		return node.State == reflection.REVISION_DRAFT;
	}

	return declare(Widget, {
		allowChildToScroll: true,
		plugins: null,
		templateString: template,
		widgetsInTemplate: false,

		// make sure can be able to go through the logic in resize function
		bViewed: true,

		// breadcrumb view always exists, but can be refreshed in middle of action, making action controller's widget invalid; action processing should continue in that case
		omitValidActionCheck: true,

		// DOMNode: nodes in view
		breadcrumbToolbarNode: null,
		dashboardNavigationNode: null,
		revisionNode: null,
		simpleRevisionNode: null,
		trailNode: null,

		// Crumb: crumb for current node
		crumbCurrent: null,

		/** @property {boolean} renderRevisionAndActions - set to true to only render state and revision information / actions */
		renderRevisionAndActions: false,

		/** @property {RevisionDropDownButton} _revisionDropDown - drop down button for revisions */
		_revisionDropDown: null,

		getActionControllerContexts: function (/*String*/idAction) {
			// summary: if action is revision action, then lookup controller by node type first, then view type
			var contexts = this.inherited(arguments);

			if (this.getRevisionNode() && _stateActions[idAction]) {
				contexts = [routerStore.locationType].concat(contexts);
			}

			return contexts;
		},

		/**
		 * Return list of crumbs in breadcrumb
		 * @return {Crumb[]}
		 */
		getCrumbs: function () {
			return html.getChildWidgets(this.trailNode).filter(w => w instanceof Crumb);
		},

		getRevisionNode: function () {
			// summary: return selected revision for current node
			return this.cacheView.getNode(routerStore.locationRevisionUrl);
		},

		onViewDisable: function (/*Boolean*/disabled) {
			// summary: disable widgets in breadcrumb trail
			html.getChildWidgets(this.trailNode).forEach(w => w.set("disabled", disabled));
		},

		postCreate: function () {
			// summary: reserve location key for current node
			var defView = this.defView;

			// add phase/revision actions for breadcrumb view
			defView._addViewAction(ACTION_REVISION_ALL, {
				m_viewRef: lang.mixin(new ViewRef(), {
					m_id: "Revisions"
				})
			});

			defView._addViewAction(ACTION_SIMPLE_APPROVE, {
				classToolbarPlugin: ToolbarPluginButton,
				m_class: "csi-primary-btn",
				m_onAction: "_onActionSimpleApprove",
				m_postAction: "_postActionSimpleApprove"
			});

			defView._addViewAction(ACTION_REVISION, {
				m_postAction: "_postActionChangeRevision"
			});

			// resolve revision headings
			var lr = locale.localeResolver;
			var rc = reflection.getClass(reflection.TYPE_REVISION);

			_headingName.resolveHeading(lr, null, rc);
			_headingPhase.resolveHeading(lr, null, rc);
			_headingState.resolveHeading(lr, null, rc);

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Resize the widget after initialization of toolbar
		 */
		postAction: function (ac, bKeepDisabled) {
			const result = this.inherited(arguments);
			this.resize();

			return result;
		},

		/**
		 * Notify the receiver with the new published primary type
		 * @param {Controller} ac
		 * @return {Promise}
		 */
		_postActionSimpleApprove: function (ac) {
			topic.publish("/csi/breadcrumb/simple/approve", ac.getNode());
			return this.postActionRefresh(ac);
		},

		/**
		 * Return true if context support the particular state machine
		 * @return {boolean}
		 */
		_allowSimpleRevisionBehavior: function () {
			return this.getViewNode().StateMachine == "SimpleApprove";
		},

		_allowRevisionBehavior: function () {
			// summary: return true if revision behavior allowed for current node
			// C8-17974, Disabling the revision/state actions for "PrintDesignRevision" type, will be enabled in next released.
			const revisionNode = this.getRevisionNode();

			if (!revisionNode || revisionNode.$Type == "PrintDesignRevision") {
				return false;
			}

			var node = this.getViewNode();
			var urlParent = node.__Parent__;
			var rc = reflection.getNodeClass(node);
			var rcParent = rc.getParentClass();

			// The default logic is that if the data sheet has a single parent class (rcParent), but the instance does not have a parent,
			// or the parent is one of singleton nodes that owns templates (Routing, SupplierItem), then do not allow revision behavior.
			// NOTE: There are too many variations of how revisable classes are modeled to form a better general rule. For now this works. Special cases
			// to keep in mind are Size Charts (which can be parented by Style or Shape even though preference parent is just Style),
			// Routing and SupplierItems where the templates are owned by a site-level node and should not allow revision behavior, and
			// Documents which are owned as well, but should allow revision behavior. TestSpec, SubRouting and ContractualDocument are other
			// site level revisable nodes where revision behavior should be allowed.
			if (rcParent) {
				if (!xml.isValidURL(urlParent) && !_.contains(GLOBAL_MASTER_TYPES, node.$Type)) {
					return false;
				}

				if (_.contains(TEMPLATE_PARENTS, urlParent)) {
					return false;
				}
			}

			// Workflow is a revisable class, but the revision behavior is never exposed; so it doesn't follow any of the other patterns above.
			if (node.IsSampleBOM ||
				node.$Type === "Workflow" ||
				(node.$Type === "Document" && node.VariantName)) {
				return false;
			};

			// C8-772 : User cannot revise/freeze/approve Style Template BOM.
			var parent = this._getParentNode(node);
			if (node.$Type == "ApparelBOM" && parent != null && parent.IsTemplate) {
				return false;
			}

			return true;
		},

		// Determine if label printing is enabled for the type
		_allowLabelPrinting(type) {
			return sessionConstants.labelTemplateTypes.includes(type);

		},

		/**
		 * @override
		 * Return request necessary to render actions in breadcrumb
		 */
		_getRootRequest(node) {
			const { PATH_CURRENT_REVISION, PATH_LATEST_REVISION, getNodeClass } = reflection;
			const rc = getNodeClass(node);
			const defType = def.getNodeTypeDef(node);
			const paths = [];

			if (rc.isMaster()) {
				paths.push(PATH_CURRENT_REVISION);
				paths.push(PATH_LATEST_REVISION);
			}

			defType?.m_actions?.forEach((a) => {
				def.getActionDependencyPaths(a, paths);
			});

			if (paths.length) {
				return comm.getNodeRequest(node.$URL, null, true).addDependencyPaths(paths);
			}

			return null;
		},

		_getTrailName: function (/*Node*/node) {
			// summary: name for root nodes are handled specially - otherwise get node name using configured display format
			return node ? locale.getNodeConfiguredName(node, this.cacheView) : "";
		},

		/**
		 * Clear content and redraw
		 */
		_redraw: function () {
			this._deleteBreadcrumb();
			this._preView();
		},

		/**
		 * Control the navigation button by given node
		 * @property {Node} node
		 */
		_buildDashboardBtnNode: function (node) {
			const { dashboardNavigationNode, renderRevisionAndActions } = this;
			const rc = reflection.getNodeClass(node);
			const bShow = !rc.isSingleton() && !renderRevisionAndActions;

			html.destroyChildren(dashboardNavigationNode);

			if (bShow) {
				const { lastAdminMode } = localStore;
				const { homeUrl, siteUrl } = sessionStore;

				new MaterialIconWithLink({
					icon: lastAdminMode ? "build" : "home",
					href: session.getLocationForUrl(lastAdminMode ? siteUrl : homeUrl)
				}).placeAt(dashboardNavigationNode);
			}

			html.setVisible(dashboardNavigationNode, bShow);
		},

		/**
		 * Get actions that are not links to be added into the breadcrumb dropdown menu actions
		 * @param {Action[]} actions
		 * @returns {Action[]}
		 */
		_getActionsExcludeLink(actions) {
			return actions.filter(action => action.m_typeControl != def.ACTION_LINK);
		},

		/**
		 * Get actions that are links
		 * @param {Action[]} actions
		 * @returns {Action[]}
		 */
		_getLinkActions(actions) {
			return actions.filter(action => action.m_typeControl == def.ACTION_LINK);
		},

		_buildView: function () {
			// summary: populate breadcrumb trail and actions for node
			var node = this.getViewNode();
			if (node) {
				const { renderRevisionAndActions } = this;
				var defType = def.getNodeTypeDef(node);

				// update view definition based on current node
				this.defView.m_idType = node.$Type;

				// output path to current node
				if (renderRevisionAndActions) {
					html.destroyChildren(this.trailNode);
					this._buildRevisionNode(this.trailNode);
				} else if (!this.crumbCurrent || (this.crumbCurrent.get("urlCrumb") != node.$URL)) {
					html.destroyChildren(this.trailNode);
					this._buildCrumbs();
				}

				// build the dashboard navigation button
				this._buildDashboardBtnNode(node);

				// update toolbar based on node type
				this.plugins = [];

				html.destroyChildren(this.revisionNode);
				html.destroyChildren(this.simpleRevisionNode);

				// add revision controls; the versions are invalid on "Draft" node with simple revision model
				const nodeRevision = this.getRevisionNode();
				const bRevision = this._allowRevisionBehavior();
				const bSimpleRevision = bRevision && this._allowSimpleRevisionBehavior();
				const bSimpleVersions = bSimpleRevision && !_isDraft(nodeRevision);

				html.setVisible(this.revisionNode, bRevision && (!bSimpleRevision || bSimpleVersions));
				html.setVisible(this.simpleRevisionNode, bSimpleRevision);
				this.domNode.classList.toggle("csi-breadcrumb-view-simple-revision", bSimpleRevision);

				if (bRevision) {
					if (bSimpleRevision) {
						// show simple revision control
						this._buildSimpleRevisionControl();

						if (bSimpleVersions) {
							this._buildSimpleRevisionVersion();
						}
					} else {
						// show revision information
						this._buildRevisionTrail();

						// add plugins for revision behavior
						var revisionActions = _getRevisionActions(node);

						if (revisionActions) {
							this.plugins.push({ name: "Actions", children: revisionActions });
						}
					}
				}

				if (defType && defType.m_actions) {
					if (NODES_WITH_DROPDOWN_ACTIONS.includes(defType.getType())) {
						this.plugins = this.plugins.concat(this._getLinkActions(defType.m_actions));
						this.plugins.push({ name: "Actions", children: this._getActionsExcludeLink(defType.m_actions) });
					} else if (NODES_WITH_PRINT_ACTIONS_DROPDOWN.includes(defType.getType())) {
						this._buildPrintDropdownActionView(nodeRevision, defType);
					} else {
						this.plugins = this.plugins.concat(defType.m_actions);
					}
				}



				this.toolbarNode = new Toolbar({
					bHideDisabledMenus: true,
					bMenuControlHide: true,
				}, domConstruct.create("div", null, this.breadcrumbToolbarNode));

				this.toolbarNode.setView(this);
				this.toolbarNode.disablePlugins();

				// output link(s) to search for current node's children, but only if host user and
				// if Enable Breadcrumb Search is set to true for the played role
				if (defType && sessionConstants.enableBreadcrumbSearch && !renderRevisionAndActions) {
					this._buildSearches(node, defType.getChildSearches());
				}

				html.setVisible(this.domNode, true);
				this._onResize();

				// set context of breadcrumb trail
				html.setElementContext(this.domNode, node.$URL);
			} else {
				this.crumbCurrent = null;
				html.setVisible(this.domNode, false);
			}

			this.inherited(arguments);
		},

		// Add the Label Actions dropdown to the plugins
		_buildPrintDropdownActionView(nodeRevision, defType) {
			if (this._allowLabelPrinting(nodeRevision.$Type)) {
				this.plugins.push({ name: "LabelActions", children: defType.m_actions, showDisabledItems: true });
			} else {
				this.plugins = this.plugins.concat(defType.m_actions[0]);
			}
		},

		/**
		 * Build trail of nodes in breadcrumb
		 */
		_buildCrumbs() {
			const { cacheView, urlRoot } = this;
			let foundRoot = false;

			const breadcrumbNodes = routerStore.breadcrumbUrls.reduce((memo, url, i, a) => {
				const node = cacheView.get(url);
				const rc = reflection.getNodeClass(node);

				if (rc.isSingleton()) {
					// reset memo if singleton node, forgetting any earlier nodes; otherwise add node and continue
					// FIX: not sure which use case this satisfies where a singleton node might have previous nodes
					// in the breadcrumb; this logic could be moved to the server to prevent this
					return [node];
				} else if (foundRoot) {
					// once you get to root node, no need to get more nodes (such as revision)
					return memo;
				}

				foundRoot = urlRoot === url;

				return [...memo, node];
			}, []);

			// render nodes in breadcrumb
			this.crumbCurrent = breadcrumbNodes.reduce((memo, node, i, a) => {
				const href = def.getNodeBrowseURL(node);
				const previousNode = a[i - 1];
				const previousHref = previousNode && def.getNodeBrowseURL(previousNode);

				// if you cannot navigate to this node, or it was the same link as before, then skip
				if (!href || (href === previousHref)) {
					return null;
				}

				return this._buildCrumb(node, previousNode);
			}, null);
		},

		/**
		 * Build component for single node in breadcrumb
		 * @param {C8Node} node
		 * @param {C8Node} previousNode
		 * @return {Crumb}
		 */
		_buildCrumb(node, previousNode) {
			const bCurrent = (node.$URL === this.urlRoot);
			let href = bCurrent ? null : def.getNodeBrowseURL(node);
			let rc = reflection.getNodeClass(node);
			let bTop = rc.isSingleton();
			let defAction;
			let store;

			if (!bTop && sessionConstants.enableBreadcrumbSearch) {
				// do not allow searching on breadcrumb for non-host users - could give them access to nodes they are not supposed to see
				// do not allow searching on breadcrumb for users who play role that has Enable Breadcrumb Search disabled
				let idAction;

				// some templates may have parents, but use global store to get correct action id
				const isTemplate = reflectionHints.isTemplateNode(node);

				if (previousNode && !isTemplate) {
					store = SearchManager.getSiblingStore(node, previousNode);
					idAction = store ? "CrumbSearchContext" : null;
				} else if (!previousNode || isTemplate || SearchManager.getSearchType(node.$Type)) {
					store = SearchManager.getGlobalStore(node);
					idAction = store ? "CrumbSearchGlobal" : null;
				}

				// create action for crumb search
				if (idAction) {
					defAction = lang.mixin(new Action(), { m_id: idAction });
				}
			}

			// embed current crumb in extra div as we need this crumb to have a minimum width to ensure there is enough space for the search box,
			// and we don't want the hover background color on the crumb below to affect leftover space
			let domNode = this.trailNode;
			let pos;

			if (bCurrent) {
				domNode = html.create("div", { className: "csi-crumb-current" }, domNode);
				pos = "first";

				// create sibling node to ensure minimum width, and revision node under it to hold any revision information
				const div = html.create("div", { className: "csi-crumb-current-revision" }, domNode);
				this._buildRevisionNode(div);
				this._buildStateHeading(div);
			}

			const parent = this._getParentNode(node);
			return new Crumb({
				"class": (store ? "crumbSearch" : (parent ? "" : "crumbSearchOff")),
				defAction,
				href,
				store,
				status: reflectionHints.getLocationNodeStatus(node, bCurrent),
				text: this._getTrailName(node),
				typeCrumb: node.$Type,
				urlCrumb: node.$URL,
				isArchiveNode: xml.nodeIsArchived(node),
				onChange: url => this._navigate(url)
			}).placeAt(domNode, pos);
		},

		/**
		 * Create DOMNode to contain revision components
		 * @param {DOMNode} domNode
		 */
		_buildRevisionNode: function (domNode) {
			this.revisionNode = html.create("div", { className: "csi-breadcrumb-revision" }, domNode);
		},

		/**
		 * Add the action and render the simple revision control
		 * @param {Node} - node
		 * @param {boolean} bDraft - indicate whether or not draft
		 * @param {boolean} bToggle - the state of the radio button
		 * @param {boolean} bTwoState - indicates if two actions are both available
		 */
		_addSimpleRevisionAction: function (node, bDraft, bToggle, bTwoState) {
			const domNode = this.simpleRevisionNode;
			const actionName = bDraft ? "Draft" : "Published";
			var className;

			if (!bTwoState) {
				className = "csi-shaded-toolbar csi-no-pointer-events csi-button-toggle" + (bDraft ? " csi-toggle-group-first" : " csi-toggle-group-last");
			} else {
				className = "csi-shaded-toolbar csi-button-toggle csi-breadcrumb-simple-revision-control-" + (bDraft ? "left csi-toggle-group-first" : "right csi-toggle-group-last");
			}

			const action = lang.mixin(new Action(), {
				m_id: actionName,
				m_name: locale.resource(actionName),
				m_bToggle: bToggle && bTwoState,
				m_onAction: function (ac) {
					ac.actionValue = node.$URL;
				},
				m_postAction: "_postActionChangeRevision"
			});

			action.resolve();

			new ToolbarPluginRadioButton({
				defAction: action,
				radioClass: "csi-breadcrumb-simple-revision-control",
				"class": className
			}).placeAt(domNode);
		},

		/**
		 * Build simple revision control based on the state of the revision
		 */
		_buildSimpleRevisionControl: function () {
			const masterNode = this.getViewNode();
			const cache = this.getCache();

			const currentRevision = cache.get(masterNode.CurrentRevision);
			const latestRevision = cache.get(masterNode.LatestRevision);

			const draftNode = _isDraft(latestRevision) ? latestRevision : null;
			const publishedNode = !_isDraft(currentRevision) ? currentRevision : null;

			const revisionNode = this.getRevisionNode();
			const bDraft = _isDraft(revisionNode);
			const bTwoState = !!draftNode && !!publishedNode;

			if (draftNode) {
				this._addSimpleRevisionAction(draftNode, true, bDraft, bTwoState);
			}

			if (publishedNode) {
				this._addSimpleRevisionAction(publishedNode, false, !bDraft, bTwoState);
			}

			if (bDraft && crews.isInRoles(["Configuration Editor"], this.getRevisionNode())) {
				// Build the "Publish" button to approve, only available when the current revision is draft
				this.plugins.push(this.getAction(ACTION_SIMPLE_APPROVE));
			}
		},

		/**
		 * Call the server operation to approve the current revision
		 * @param {Controller} ac
		 */
		_onActionSimpleApprove: function (ac) {
			ac.m_writer.callMethod("SiteAdmin", "Approve").addParameter(xml.PROP_URL, xml.ATTR_REF, routerStore.locationRevisionUrl);
		},

		/**
		 * Build simple revision versions if needed
		 */
		_buildSimpleRevisionVersion: function () {
			this._revisionVersionDropDown = new RevisionVersionDropDownButton({
				"class": "csi-pill-btn",
				revisionAction: this.getAction(ACTION_REVISION),
				revisionType: "CamundaProcessDefinitionRevision",
				view: this
			}).placeAt(this.revisionNode);
		},

		_buildRevisionTrail: function () {
			// summary: render information about the revision
			var nodeRevision = this.getRevisionNode();
			var ctx = this.getContext(nodeRevision.$URL);
			var domNode = this.revisionNode;

			if (xml.isValidURL(nodeRevision.Phase)) {
				new PhaseDropDownButton({
					"class": "csi-pill-btn",
					revisionAction: this.getAction(ACTION_REVISION),
					view: this
				}).placeAt(domNode);
			}

			_renderHeading(ctx, _headingState, domNode);

			this._revisionDropDown = new RevisionDropDownButton({
				"class": "csi-pill-btn",
				revisionAction: this.getAction(ACTION_REVISION),
				revisionsAction: this.getAction(ACTION_REVISION_ALL),
				view: this
			}).placeAt(domNode);
			const { linkedNode } = routerStore;
			if (linkedNode) {
				_renderLinkedHeading(ctx, _headingState, domNode, linkedNode);
			}
		},

		/**
		 * Build node's state heading on the breadcrumb
		 * @param {DOMNode} domNode
		 */
		_buildStateHeading: function (domNode) {
			const node = this.getViewNode();
			const defType = def.getNodeTypeDef(node);
			const id = defType.stateHeading;

			if (id) {
				const ctx = this.getContext(node.$URL);
				const h = lang.mixin(new Heading(), { m_id: id });
				const lr = locale.localeResolver;
				const rc = reflection.getNodeClass(node);

				h.resolveHeading(lr, null, rc);
				_renderHeading(ctx, h, domNode);
			}
		},

		_buildSearches: function (/*Node*/node, /*SearchChild[]*/searches) {
			// summary: build crumbs for context searches below current node
			var rc = reflection.getNodeClass(node);
			var tb = this.toolbarNode;

			if (rc && tb && searches) {
				var first = tb.getFirstPluginName();

				_.each(searches, function (defSearch) {
					var rcTarget = defSearch.rcTarget;
					if (!rcTarget) return;

					// no need to show child search if no children
					var id = defSearch.id;
					var path = defSearch.path;
					if (id && !path && (!node[id] || !node[id].length)) {
						return;
					}

					// check to make sure child class is not abstract and supports views
					var type = rcTarget.getType();

					if (def.canNavigateToType(type) || (rcTarget.abstract && rcTarget.getConcreteClasses())) {
						// display format must come from base class - otherwise filtering will not work if each derived class has different format
						var store = SearchManager.getPathStore(node, defSearch.limitPath, type);

						var box = new CrumbChild({
							defAction: lang.mixin(new Action(), { m_tag: "CrumbSearchChild" }),
							defSearch,
							pluginName: "Crumb" + defSearch.name,
							store,
							actionToolbar: this.toolbarNode
						}, domConstruct.create("input", null, null));

						tb.addPluginWidget(box, first);
					}
				}, this);
			}
		},

		_deleteBreadcrumb: function () {
			// summary: reset breadcrumb to default
			this.crumbCurrent = null;
			this.revisionNode = null;
			this._revisionDropDown = null;

			html.destroyChildren(this.trailNode);
			html.destroyChildren(this.simpleRevisionNode);
			this._destroyToolbar();
		},

		_getParentNode: function (/*Node*/node) {
			// find parent node - if not already in cache, then query all the way up parent chain in single request
			return this.cacheView.getNode(node.__Parent__);
		},

		/**
		 * Navigate to selected node
		 */
		_navigate: function (url) {
			session.setLocationUrl(url);
		},

		/**
		 * @override
		 * If any status nodes are truncated due to overflow, then hide them
		 */
		_onResize: function () {
			const classList = this.trailNode.classList;
			const css = "csi-breadcrumb-hide-status";
			classList.remove(css);
			classList.toggle(css, this.getCrumbs().some(crumb => crumb.overflowsStatus()));
		},

		_postActionChangeRevision: function (/*Controller*/ac) {
			// summary: switch to selected revision
			var urlRevision = ac.actionValue;
			if (urlRevision != routerStore.locationRevisionUrl) {
				session.setLocationKey(session.LOCATION_REVISION_URL, urlRevision, true);
			}
		}
	});
});